// Palette-indexed pixel assets for the studio tools (docs/ASSET_STUDIO.md). No DOM, no node: shared by the game,
// the tests and both studios. Every pixel is an index into ASSET_TOKENS, so no color outside the palette can enter.

/** Palette references, never arbitrary RGB: all art resolves through the game's hd-kit ramps (`K`).
 *  The order is a format contract: append only (tests/world-edits.test.ts snapshots the first 38). */
export const ASSET_TOKENS = [
  "transparent",
  "ink",
  "night",
  "slate",
  "white",
  "cyan",
  "pink",
  "amber",
  "metal",
  "metalHi",
  "metalLo",
  "concrete",
  "concreteHi",
  "concreteLo",
  "glass",
  "glassHi",
  "glassLo",
  "wood",
  "woodHi",
  "woodLo",
  "leaf",
  "leafHi",
  "leafLo",
  "paint",
  "paintLo",
  "warm",
  "warmHi",
  "warmLo",
  "red",
  "redLo",
  "redDk",
  "asphaltLo",
  "plaster",
  "plasterLo",
  "walk",
  "walkHi",
  "walkLo",
  "seam",
] as const;
export type AssetToken = (typeof ASSET_TOKENS)[number];
export const ASSET_FAMILIES = [
  "vending-machine",
  "arcade-cabinet",
  "utility-cabinet",
  "neon-sign",
  "planter",
  "bench",
  "lantern",
  "trash-bin",
  "barrier",
  "charging-station",
  "bollard",
  "air-conditioner",
  "food-cart",
  "manhole-cover",
  "storm-drain",
  "tactile-paving",
  "bike-rack",
  "traffic-cone",
  "sandwich-board",
  "menu-stand",
  "shop-awning",
  "stacked-crates",
  "delivery-boxes",
  "payphone",
  "capsule-machine",
  "parking-meter",
  "hanging-sign",
  "banner-flag",
  "pipes",
  "cables",
  "trash-bags",
] as const;
export type AssetFamily = (typeof ASSET_FAMILIES)[number];
/** Lie flat on the ground: painted into the scene ground (players walk over them), no shadow, may turn 180 degrees. */
export const FLAT_FAMILIES: readonly AssetFamily[] = [
  "manhole-cover",
  "storm-drain",
  "tactile-paving",
];
export const isFlatFamily = (f: AssetFamily): boolean =>
  FLAT_FAMILIES.includes(f);
export interface AssetRecipe {
  family: AssetFamily;
  seed: number;
  accent: "pink" | "cyan" | "amber";
  material: "metal" | "concrete" | "plaster";
  width: number;
  height: number;
  wear: number;
  glow: number;
  detail: number;
  label: string;
}
/** x0, y0, w, h of the non-transparent pixels, in canvas pixels. */
export type PixelBox = [number, number, number, number];
export interface PixelAsset {
  id: string;
  name: string;
  prompt: string;
  recipe: AssetRecipe;
  /** Canvas size in art px (2 art px per world px); the foot anchor `ax, ay` is in canvas px. */
  width: number;
  height: number;
  ax: number;
  ay: number;
  /** Bounding box of the drawn pixels inside the canvas. */
  box: PixelBox;
  /** Run-length encoded pixels of `box`, row by row: a letter (A = transparent, B = ink, ... the ASSET_TOKENS index
   *  in the alphabet A-Za-z) followed by its run length in decimal, omitted when 1. Example: "A12B3C". */
  pixels: string;
}
export interface AssetPack {
  type: "neon-kabuki-asset-pack";
  version: 1;
  assets: PixelAsset[];
}
export const MAX_PACK_ASSETS = 32;
const RUN_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const bad = (m: string) => {
  throw Error(m);
};

export function readRecipe(value: unknown): AssetRecipe {
  const r = value as AssetRecipe;
  if (
    !r ||
    !ASSET_FAMILIES.includes(r.family) ||
    !Number.isInteger(r.seed) ||
    r.seed < 0 ||
    r.seed > 0xffffffff ||
    !["pink", "cyan", "amber"].includes(r.accent) ||
    !["metal", "concrete", "plaster"].includes(r.material) ||
    !Number.isInteger(r.width) ||
    r.width < 24 ||
    r.width > 64 ||
    !Number.isInteger(r.height) ||
    r.height < 24 ||
    r.height > 88 ||
    ![r.wear, r.glow, r.detail].every(
      (n) => Number.isInteger(n) && n >= 0 && n <= 100,
    ) ||
    typeof r.label !== "string" ||
    r.label.length > 12 ||
    !/^[A-Z0-9 .!+-]*$/.test(r.label)
  )
    bad(
      "Invalid asset recipe. Use the supported family, theme materials and bounded controls.",
    );
  return {
    family: r.family,
    seed: r.seed,
    accent: r.accent,
    material: r.material,
    width: r.width,
    height: r.height,
    wear: r.wear,
    glow: r.glow,
    detail: r.detail,
    label: r.label,
  };
}

/** Trim a full-canvas pixel array (width x height token indices) to its bounding box and run-length encode it. */
export function packPixels(
  full: Uint8Array,
  width: number,
  height: number,
): { box: PixelBox; pixels: string } {
  let x0 = width,
    y0 = height,
    x1 = -1,
    y1 = -1;
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++)
      if (full[y * width + x]) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        y1 = y;
      }
  if (x1 < 0) return { box: [0, 0, 1, 1], pixels: "A" }; // nothing drawn
  const w = x1 - x0 + 1,
    h = y1 - y0 + 1;
  let out = "",
    run = 0,
    last = -1;
  const flush = () => {
    if (run) out += RUN_LETTERS[last] + (run > 1 ? run : "");
  };
  for (let y = y0; y <= y1; y++)
    for (let x = x0; x <= x1; x++) {
      const v = full[y * width + x];
      if (v === last) run++;
      else {
        flush();
        last = v;
        run = 1;
      }
    }
  flush();
  return { box: [x0, y0, w, h], pixels: out };
}

/** Expand `pixels` to exactly `count` token indices; throws on a malformed string or an index outside the palette. */
function unpackRuns(pixels: string, count: number): Uint8Array {
  if (!/^(?:[A-Za-z]\d{0,5})+$/.test(pixels))
    bad("Invalid pixel asset: malformed pixel data.");
  const out = new Uint8Array(count);
  let n = 0;
  for (const m of pixels.matchAll(/([A-Za-z])(\d*)/g)) {
    const v = RUN_LETTERS.indexOf(m[1]),
      run = m[2] ? Number(m[2]) : 1;
    if (v >= ASSET_TOKENS.length)
      bad("Asset uses a color outside the Neon Kabuki palette.");
    if (run < 1 || n + run > count)
      bad("Invalid pixel asset: pixel data does not fit its box.");
    out.fill(v, n, n + run);
    n += run;
  }
  if (n !== count) bad("Invalid pixel asset: pixel data does not fit its box.");
  return out;
}

/** The whole canvas (width x height) as token indices. */
export function decodePixels(asset: PixelAsset): Uint8Array {
  const [x0, y0, w, h] = asset.box,
    part = unpackRuns(asset.pixels, w * h),
    full = new Uint8Array(asset.width * asset.height);
  for (let y = 0; y < h; y++)
    full.set(part.subarray(y * w, (y + 1) * w), (y0 + y) * asset.width + x0);
  return full;
}

export function readAsset(value: unknown): PixelAsset {
  const a = value as PixelAsset & { box?: unknown };
  if (
    !a ||
    typeof a.id !== "string" ||
    !a.id ||
    a.id.length > 100 ||
    typeof a.name !== "string" ||
    !a.name.trim() ||
    a.name.length > 100 ||
    typeof a.prompt !== "string" ||
    a.prompt.length > 500 ||
    !Number.isInteger(a.width) ||
    a.width < 16 ||
    a.width > 128 ||
    !Number.isInteger(a.height) ||
    a.height < 16 ||
    a.height > 128 ||
    !Number.isInteger(a.ax) ||
    !Number.isInteger(a.ay) ||
    a.ax < 0 ||
    a.ay < 0 ||
    a.ax >= a.width ||
    a.ay >= a.height ||
    typeof a.pixels !== "string"
  )
    bad(
      "Invalid pixel asset: bounded dimensions, foot anchor and palette-indexed pixels are required.",
    );
  let full: Uint8Array;
  if (a.box === undefined) {
    // Older exports: two hex digits per pixel for the whole canvas.
    if (
      a.pixels.length !== a.width * a.height * 2 ||
      !/^[0-9a-f]+$/.test(a.pixels)
    )
      bad("Invalid pixel asset: pixel data does not match the canvas.");
    full = new Uint8Array(a.width * a.height);
    for (let i = 0; i < full.length; i++) {
      full[i] = parseInt(a.pixels.slice(i * 2, i * 2 + 2), 16);
      if (full[i] >= ASSET_TOKENS.length)
        bad("Asset uses a color outside the Neon Kabuki palette.");
    }
  } else {
    const b = a.box as number[];
    if (
      !Array.isArray(b) ||
      b.length !== 4 ||
      !b.every(Number.isInteger) ||
      b[0] < 0 ||
      b[1] < 0 ||
      b[2] < 1 ||
      b[3] < 1 ||
      b[0] + b[2] > a.width ||
      b[1] + b[3] > a.height
    )
      bad("Invalid pixel asset: the pixel box must sit inside the canvas.");
    full = decodePixels({ ...a, box: b as PixelBox } as PixelAsset);
  }
  return {
    id: a.id,
    name: a.name,
    prompt: a.prompt,
    recipe: readRecipe(a.recipe),
    width: a.width,
    height: a.height,
    ax: a.ax,
    ay: a.ay,
    ...packPixels(full, a.width, a.height),
  };
}

/** Same drawing (the studios ask before replacing an asset id whose pixels differ). */
export const sameAsset = (a: PixelAsset, b: PixelAsset): boolean =>
  a.pixels === b.pixels &&
  a.box.join() === b.box.join() &&
  a.ax === b.ax &&
  a.ay === b.ay;

/** A list of assets with unique ids; `max` is the caller's own cap (a pack, a world draft). */
export function readAssetList(value: unknown, max: number): PixelAsset[] {
  if (!Array.isArray(value) || value.length > max)
    bad(`Expected a list of at most ${max} assets.`);
  const assets = (value as unknown[]).map(readAsset);
  if (new Set(assets.map((a) => a.id)).size !== assets.length)
    bad("Duplicate asset IDs.");
  return assets;
}
export function readAssetPack(value: unknown): AssetPack {
  const p = value as AssetPack;
  if (!p || p.type !== "neon-kabuki-asset-pack" || p.version !== 1)
    bad(
      `Expected a version 1 Neon Kabuki asset pack with at most ${MAX_PACK_ASSETS} assets.`,
    );
  return {
    type: "neon-kabuki-asset-pack",
    version: 1,
    assets: readAssetList(p.assets, MAX_PACK_ASSETS),
  };
}
